import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import clsx from 'classnames';
import { injectIntl, FormattedMessage } from 'react-intl';
import {
  makeStyles,
  Button,
  Modal,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  colors,
  LinearProgress,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { v1 as uuid } from 'uuid';
import { useDropzone } from 'react-dropzone';
import PerfectScrollbar from 'react-perfect-scrollbar';
import bytesToSize from 'utils/bytesToSize';
import SubLoader from 'components/SubLoader';
import { isCorrectFileName } from 'utils/validations';
import { ReactComponent as UploadIcon } from 'assets/svgs/doc_upload.svg';

const useStyles = makeStyles(theme => ({
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 700,
    maxHeight: '100%',
    overflowY: 'auto',
    // maxWidth: '98%',
    borderRadius: 10,
    [theme.breakpoints.down('xs')]: {
      width: '93%',
    },
  },
  closeIcon: {
    position: 'absolute',
    right: theme.spacing(3),
    top: 22,
    cursor: 'pointer',
    width: theme.spacing(4),
    height: theme.spacing(4),

    fill: 'white',

    '& path': {
      fill: 'white',
    },
  },
  actions: {
    paddingTop: 0,
    paddingBottom: theme.spacing(3)
  },
  header: {
    padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
    display: 'flex',
    backgroundColor: theme.palette.app.primary,
  },
  icon: {
    width: theme.spacing(2.1),
    height: theme.spacing(2.1),
    marginRight: theme.spacing(1),

    fill: 'white',
    '& path': {
      fill: 'white',
    },
  },
  headertitle: {
    fontSize: 16,
    color: 'white',
    fontWeight: 'bold',
    lineHeight: '40px',
    marginRight: theme.spacing(2)
  },
  iconRemove: {
    marginRight: 0,
    fill: colors.blueGrey[900],
    '& path': {
      fill: colors.blueGrey[900],
    },
  },

  //
  dropZone: {
    border: `1px solid #E5E3F2`,
    backgroundColor: '#F8F9FD',
    padding: theme.spacing(4, 6),
    outline: 'none',
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    alignItems: 'center',
    borderRadius: 5,
    '&:hover': {
      backgroundColor: colors.grey[50],
      opacity: 0.5,
      cursor: 'pointer',
    },
  },
  dragActive: {
    backgroundColor: colors.grey[50],
    opacity: 0.5,
  },
  info: {
    marginTop: theme.spacing(1),
    color: theme.palette.app.primary
  },
  list: {
    maxHeight: 320,
    overflowY: 'auto'
  },
  progressWrapper: {
    margin: `${theme.spacing(2.5)}px 0`,
  },
  validation: {
    textAlign: 'center',
    marginTop: theme.spacing(1)
  }
}));

const FileUploadModal = ({ onConfirm, onClose, visible, loading, index, intl: { formatMessage }, addFree }) => {
  const classes = useStyles();

  const [files, setFiles] = useState([]);

  const handleDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.some(file => !isCorrectFileName(file.name))) {
      return alert("Invalid file name!")
    } 
    setFiles(prevFiles =>
      [...prevFiles].concat(
        acceptedFiles.filter(file => isCorrectFileName(file.name)),
      ),
    );
  }, []);

  const handleRemoveAll = () => {
    setFiles([]);
  };

  const removeFile = i => {
    setFiles(files => files.filter((file, index) => index !== i));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: 'application/msword,application/vnd.ms-excel,application/vnd.ms-powerpoint,text/plain, application/pdf,audio/*,video/*,image/*'
  });

  const isDisabled = files.length === 0;

  const totalFileSize =
    files.length > 0
      ? files.map(file => file.size).reduce((e1, e2) => e1 + e2)
      : 0;

  const uploadedFileSize =
    index > 0 && files.length > 0
      ? files.slice(0, index).map(file => file.size).reduce((e1, e2) => e1 + e2)
      : 0;

  const renderHeader = () => (
    <div className={classes.header}>
      <Typography className={classes.headertitle}>{formatMessage({ id: "document.add" })}</Typography>
      <CloseIcon className={classes.closeIcon} onClick={onClose} />
    </div>
  )
  return (
    <Modal
      open={visible}
      onClose={() => {
        if (!loading) {
          onClose();
        }
      }}
    >
      <Card className={classes.modal}>
        <CardHeader
          component={renderHeader}
        />
        {loading && <SubLoader />}
        <CardContent>
          {loading && (
            <div className={classes.progressWrapper}>
              <LinearProgress
                color="primary"
                variant="determinate"
                value={uploadedFileSize * 100 / totalFileSize}
              />
              {index < files.length && (
                <Typography align="center" paragraph>
                  Chargement {files[index].name} ... ({bytesToSize(uploadedFileSize)}/{bytesToSize(totalFileSize)})
                </Typography>
              )}
            </div>
          )}
          {!loading && (
            <div
              className={clsx({
                [classes.dropZone]: true,
                [classes.dragActive]: isDragActive,
              })}
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              <div style={{ textAlign: 'center' }}>
                <UploadIcon />
                <Typography gutterBottom variant="h4">
                  <strong>{formatMessage({ id: "document.uploadfiles" })}</strong>
                </Typography>
                <Typography
                  className={classes.info}
                  color="textSecondary"
                  variant="body1"
                >
                  {formatMessage({ id: "document.droporclick" })}
                </Typography>
                {files.length > 0 && (
                  <div>
                    <PerfectScrollbar options={{ suppressScrollX: true }}>
                      <List className={classes.list}>
                        {files.map((file, i) => (
                          <ListItem divider={i < files.length - 1} key={uuid()}>
                            <ListItemText
                              primary={file.name}
                              primaryTypographyProps={{ variant: 'h5' }}
                            />
                            <IconButton
                              edge="end"
                              onClick={e => {
                                e.persist();
                                removeFile(i);
                              }}
                            >
                              <CloseIcon
                                className={clsx(classes.icon, classes.iconRemove)}
                              />
                            </IconButton>
                          </ListItem>
                        ))}
                      </List>
                    </PerfectScrollbar>
                  </div>
                )}
              </div>
            </div>
          )}
          <Typography paragraph className={classes.validation}>
            {formatMessage({ id: "documents.validation" })}
          </Typography>

        </CardContent>
        <CardActions className={classes.actions}>
          {
            addFree &&
            <Button
              variant="contained"
              onClick={() => {
                window.open("https://console-espaceclient.apave.com/doc/formation.xlsx", '_blank', 'noopener')
              }}
              style={{ borderRadius: 8 }}
            >
              TELECHARGER LE FICHIER D'IMPORT
            </Button>
          }
          <Button
            variant="contained"
            onClick={() => addFree ? onConfirm(files[0], files[0].type) : onConfirm(files)}
            disabled={isDisabled}
            style={{ borderRadius: 8 }}
          >
            <FormattedMessage id="save" />
          </Button>
          &nbsp;&nbsp;&nbsp;
          <Button
            onClick={handleRemoveAll}
            variant="outlined"
            disabled={isDisabled}
            style={{ borderRadius: 8 }}
          >
            {formatMessage({ id: "documents.removeall" })}
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
};

FileUploadModal.propTypes = {
  addFree: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }),
  index: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
};

FileUploadModal.defaultProps = {};

export default injectIntl(FileUploadModal);
